import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button } from "react-admin";
import dataProvider from "server/dataProvider";
import { dormitoryRequests } from "api/dormitoryApi";
import { useNotifyError } from "utils/useNotifyError";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { currentRoomLabel, hasPendingMove, pendingRoomLabel } from "../Partials/roomLabels";
import { useSourceRoomLabel } from "../Partials/useSourceRoomLabel";
import { formatDateOnlyBg } from "server/formatting/formatDateBg";

// StudentRightsStatus, numbered from 1 in the backend: Active/Interrupted/Suspended.
const STUDENT_RIGHTS_STATUS_LABELS = {
  1: "Активен",
  2: "Прекъснал",
  3: "Отстранен",
};

const DetailItem = ({ label, value, isOptional = false }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
      {label} {isOptional && <span style={{ fontWeight: "normal", color: "#888" }}>(по избор)</span>}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, color: value ? "text.primary" : "text.secondary" }}>
      {value || "—"}
    </Typography>
  </Box>
);

// The accommodated-student view has no application row to read from, so the student's data is fetched
// from GetById on open. Course, speciality and student-rights status are resolved by the backend from
// StudentService and AdministrationService and come back empty when those are unavailable.
const AccommodatedStudentDetails = ({ accommodationId, isExternal }) => {
  const notifyError = useNotifyError();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await dataProvider.customRequest(
        dormitoryRequests.getAccommodationById(accommodationId),
        null,
        "GET",
      );
      setDetails(data);
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  }, [accommodationId, notifyError]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  if (loading) {
    return (
      <Grid item xs={12} sx={{ textAlign: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Grid>
    );
  }

  return (
    <>
      <Grid item xs={12} sm={6}>
        <DetailItem label="Стая" value={currentRoomLabel(details)} />
      </Grid>
      {hasPendingMove(details) && (
        <Grid item xs={12} sm={6}>
          <DetailItem label="Предстоящо преместване" value={pendingRoomLabel(details)} />
        </Grid>
      )}
      {!isExternal && (
        <Grid item xs={12} sm={6}>
          <DetailItem label="Факултетен №" value={details?.facultyNumber} />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <DetailItem label="Специалност" value={details?.specialityNameBg} isOptional={isExternal} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DetailItem label="Курс" value={details?.course} isOptional={isExternal} />
      </Grid>
      {!isExternal && (
        <Grid item xs={12} sm={6}>
          <DetailItem
            label="Студентски статус"
            value={STUDENT_RIGHTS_STATUS_LABELS[details?.studentRightsStatus]}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <DetailItem label="Приложен документ" value={details?.studentVerificationDocumentId ? "Да" : null} />
      </Grid>
    </>
  );
};

// The paged row (record) only carries the identity fields shown in the table. Email, phone,
// speciality, course, student-rights status and the applicant's room preferences are
// review-only and live on the GetById detail (same call ReviewApplicationModal makes).
const SubmittedApplicationDetails = ({ record, isExternal, sourceRoom }) => {
  const notifyError = useNotifyError();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const campaignId = record?.campaignId;
  const applicationId = record?.applicationId || record?.id;

  const fetchDetails = useCallback(async () => {
    if (!campaignId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await dataProvider.customRequest(
        dormitoryRequests.getCampaignApplicationById(campaignId, applicationId),
        null,
        "GET",
      );
      setDetails(data);
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, applicationId, notifyError]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  if (loading) {
    return (
      <Grid item xs={12} sx={{ textAlign: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Grid>
    );
  }

  return (
    <>
      <Grid item xs={12} sm={6}>
        <DetailItem label="Имейл" value={details?.email} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DetailItem label="Телефон" value={details?.contactPhone} />
      </Grid>

      {!isExternal && (
        <Grid item xs={12} sm={6}>
          <DetailItem label="Факултетен №" value={record?.facultyNumber} />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <DetailItem
          label="Специалност"
          value={details?.specialityNameBg}
          isOptional={isExternal}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DetailItem label="Курс" value={details?.course} isOptional={isExternal} />
      </Grid>

      {!isExternal && (
        <Grid item xs={12} sm={6}>
          <DetailItem
            label="Студентски статус"
            value={STUDENT_RIGHTS_STATUS_LABELS[details?.studentRightsStatus]}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>

      {/* Подадени от студента при кандидатстването — не влияят на реалното разпределение по-долу. */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}>
          Предпочитания на кандидата
        </Typography>
      </Grid>

      <Grid item xs={12} sm={4}>
        <DetailItem label="Предпочитан блок" value={details?.preferredBuildingResidentialNumber} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <DetailItem label="Предпочитан тип стая" value={details?.preferredRoomTypeDescriptionBg} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <DetailItem label="Предпочитана стая" value={details?.preferredRoomNumber} />
      </Grid>
      <Grid item xs={12}>
        <DetailItem label="Предпочитан съквартирант" value={details?.preferredRoommate} />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}>
          {record?.isTransfer ? "Преместване в общежитие" : "Разпределение на общежитие"}
        </Typography>
      </Grid>

      {record?.isTransfer && (
        <Grid item xs={12} sm={6}>
          <DetailItem label="Текуща стая" value={sourceRoom} />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <DetailItem label={record?.isTransfer ? "Нов блок" : "Блок"} value={record?.assignedResidentialNumber || "Не е разпределен"} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DetailItem label={record?.isTransfer ? "Нова стая" : "Стая"} value={record?.assignedRoomNumber || "Не е разпределена"} />
      </Grid>
      {record?.appearanceDeadline && (
        <Grid item xs={12} sm={6}>
          <DetailItem
            label="Краен срок за явяване"
            value={formatDateOnlyBg(record.appearanceDeadline)}
          />
        </Grid>
      )}
    </>
  );
};

const ApplicationDetailsModal = ({ record, isExternal = false, isAccommodatedView = false }) => {
  const fullName = record?.studentFullName || "—";
  const [open, setOpen] = useState(false);
  // Application-view only: for a transfer the assigned room below is the destination, so resolve the
  // resident's current (source) room too. Passes null in the accommodated view / non-transfer case.
  const sourceRoom = useSourceRoomLabel(
    !isAccommodatedView && record?.isTransfer ? record?.sourceAccommodationId : null,
    open,
  );

  const applicationNumber = record?.applicationId || record?.id;
  const titleContent = isAccommodatedView
    ? `Преглед на настанен студент: ${fullName}`
    : `Преглед на молба${applicationNumber ? ` № ${applicationNumber}` : ""}`;

  return (
    <ConfirmModal
      onOpen={() => setOpen(true)}
      resetProps={() => setOpen(false)}
      titleContent={titleContent}
      isConfirmButton={false}
      closeTitle="Затвори"
      dialogSize="md"
      triggerButton={
        <Button
          startIcon={<VisibilityIcon />}
          label="Преглед"
        />
      }
    >
      <Grid container spacing={2} sx={{ pt: 1 }}>
        {/* 1. Основни студентски данни */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}>
            Лични и студентски данни
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <DetailItem label="Трите имена" value={fullName} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem label="ЕГН / ЛНЧ" value={record?.personId} />
        </Grid>

        {isAccommodatedView && (
          <AccommodatedStudentDetails accommodationId={record?.id} isExternal={isExternal} />
        )}

        {!isAccommodatedView && (
          <SubmittedApplicationDetails record={record} isExternal={isExternal} sourceRoom={sourceRoom} />
        )}
      </Grid>
    </ConfirmModal>
  );
};

export default ApplicationDetailsModal;
